import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { BusinessPlan as BusinessPlanType } from '../../../types/businessPlan';
import { getUserBusinessPlansFromSupabase, deleteBusinessPlanFromSupabase } from '../../../services/savedIdeasService';
import { EmptyBusinessPlans } from '../../../components/ui/EmptyState';
import { Tooltip } from 'react-tooltip';

const PlansTab: React.FC = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState<BusinessPlanType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'completed' | 'draft'>('all');

  useEffect(() => {
    loadPlans();
  }, []);

  useEffect(() => {
    const handler = () => {
      void loadPlans();
    };

    window.addEventListener('bp-data-changed', handler);
    return () => window.removeEventListener('bp-data-changed', handler);
  }, []);

  const loadPlans = async () => {
    try {
      setIsLoading(true);
      const data = await getUserBusinessPlansFromSupabase();
      setPlans(data as BusinessPlanType[]);
    } catch (error) {
      console.error('Error loading plans:', error);
      toast.error('Failed to load business plans');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (planId: string) => {
    if (!window.confirm('Are you sure you want to delete this plan?')) return;
    
    try {
      await deleteBusinessPlanFromSupabase(planId);
      setPlans(prev => prev.filter(p => p.id !== planId));
      toast.success('Plan deleted successfully');
      try {
        window.dispatchEvent(new Event('bp-data-changed'));
      } catch {
      }
    } catch (error) {
      console.error('Error deleting plan:', error);
      toast.error('Failed to delete plan');
    }
  };

  const filteredPlans = plans.filter(plan => {
    if (filter === 'all') return true;
    if (filter === 'completed') return plan.status === 'completed';
    if (filter === 'draft') return plan.status === 'draft' || plan.status === 'generating';
    return true;
  });

  const getStatusBadge = (status: string) => {
    const badges = {
      completed: 'bg-green-100 text-green-700',
      draft: 'bg-yellow-100 text-yellow-700',
      generating: 'bg-blue-100 text-blue-700'
    };
    return badges[status as keyof typeof badges] || 'bg-gray-100 text-gray-700';
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map(i => (
          <div key={i} className="animate-pulse rounded-lg border border-gray-200 p-6">
            <div className="h-5 w-3/4 bg-gray-200 rounded mb-3"></div>
            <div className="h-4 w-1/2 bg-gray-200 rounded"></div>
          </div>
        ))}
      </div>
    );
  }

  if (plans.length === 0) {
    return (
      <EmptyBusinessPlans onCreate={() => navigate('/business-plan/new')} />
    );
  }

  return (
    <div>
      {/* Filter Tabs */}
      <div className="mb-6 flex items-center gap-4 border-b border-gray-200">
        <button
          onClick={() => setFilter('all')}
          className={`pb-3 text-sm font-medium transition ${
            filter === 'all'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          All Plans ({plans.length})
        </button>
        <button
          onClick={() => setFilter('completed')}
          className={`pb-3 text-sm font-medium transition ${
            filter === 'completed'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Completed ({plans.filter(p => p.status === 'completed').length})
        </button>
        <button
          onClick={() => setFilter('draft')}
          className={`pb-3 text-sm font-medium transition ${
            filter === 'draft'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Drafts ({plans.filter(p => p.status !== 'completed').length})
        </button>
      </div>

      {/* Plans Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {filteredPlans.map(plan => (
          <div
            key={plan.id}
            className="group relative rounded-lg border border-gray-200 bg-white p-6 shadow-sm transition hover:shadow-md"
          >
            {/* Status Badge */}
            <div className="mb-3 flex items-center justify-between">
              <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getStatusBadge(plan.status || 'draft')}`}>
                {plan.status || 'draft'}
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => navigate(`/business-plan/${plan.id}`)}
                  className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                  data-tooltip-id={`bp-plan-edit-${plan.id}`}
                  data-tooltip-content="Open this plan"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                </button>
                <Tooltip id={`bp-plan-edit-${plan.id}`} place="top" />
                <button
                  onClick={() => handleDelete(plan.id || '')}
                  className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600"
                  data-tooltip-id={`bp-plan-delete-${plan.id}`}
                  data-tooltip-content="Delete this plan"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
                <Tooltip id={`bp-plan-delete-${plan.id}`} place="top" />
              </div>
            </div>

            {/* Plan Title */}
            <h3 className="mb-2 font-semibold text-gray-900 line-clamp-2">
              {plan.title || 'Untitled Plan'}
            </h3>

            {/* Metadata */}
            <div className="space-y-1 text-xs text-gray-500">
              <div className="flex items-center gap-1">
                <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span>
                  {new Date(plan.updated_at || plan.created_at || '').toLocaleDateString()}
                </span>
              </div>
              {plan.sections && (
                <div className="flex items-center gap-1">
                  <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span>
                    {Object.values(plan.sections).filter((s: any) => s.status === 'completed').length}/{Object.keys(plan.sections).length} sections
                  </span>
                </div>
              )}
            </div>

            {/* View Button */}
            <button
              onClick={() => navigate(`/business-plan/${plan.id}`)}
              className="mt-4 w-full rounded-md bg-gray-50 px-3 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-100"
            >
              View Plan
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PlansTab;
